/**
 * MarketTemperatureCard Component
 * 
 * Large visual card displaying market temperature with dynamic emojis,
 * Market Action Index gauge, and pulsing glow effects for hot markets
 */

import React from 'react';
import { Card, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThermometerHalf, faChartLine } from '@fortawesome/free-solid-svg-icons';

// Import types
import type { 
  MarketTemperatureCardProps, 
  MarketTemperature 
} from '../../../../types/marketIntelligence.types';

// Temperature configuration with emojis and colors
const TEMPERATURE_CONFIG: Record<MarketTemperature, {
  emoji: string;
  color: string;
  bgGradient: string;
  description: string;
  intensity: 'high' | 'medium' | 'low';
}> = {
  'Hot Seller Market': {
    emoji: '🔥',
    color: '#ef4444',
    bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)',
    description: 'Extremely active seller-favored market',
    intensity: 'high'
  },
  'Strong Market': {
    emoji: '📈',
    color: '#f59e0b',
    bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
    description: 'Strong activity with good momentum',
    intensity: 'medium'
  },
  'Balanced Market': {
    emoji: '⚖️',
    color: '#10b981',
    bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
    description: 'Balanced buyer-seller market conditions',
    intensity: 'medium'
  },
  'Buyer Market': {
    emoji: '📉',
    color: '#3b82f6',
    bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
    description: 'Buyer-favored market with more options',
    intensity: 'low'
  },
  'Cold Market': {
    emoji: '❄️',
    color: '#6b7280',
    bgGradient: 'linear-gradient(135deg, rgba(107, 114, 128, 0.15) 0%, rgba(75, 85, 99, 0.15) 100%)',
    description: 'Limited activity and slower sales',
    intensity: 'low'
  }
};

// Loading shimmer component
const LoadingShimmer: React.FC = () => (
  <div className="text-center p-4">
    <div 
      className="mx-auto mb-3"
      style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 2s infinite'
      }}
    />
    <div 
      className="mx-auto mb-2"
      style={{
        width: '140px',
        height: '20px',
        borderRadius: '10px',
        background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 2s infinite'
      }}
    />
    <div 
      className="mx-auto"
      style={{
        width: '80px',
        height: '12px',
        borderRadius: '6px',
        background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 2s infinite'
      }}
    />
  </div>
);

const MarketTemperatureCard: React.FC<MarketTemperatureCardProps> = ({
  temperature,
  mai,
  emoji,
  loading = false
}) => {
  // Get temperature configuration
  const tempConfig = temperature ? TEMPERATURE_CONFIG[temperature] : null;
  const displayEmoji = emoji || tempConfig?.emoji || '📊';
  const isHotMarket = temperature === 'Hot Seller Market';
  
  // Ensure mai is a valid number
  const maiValue = typeof mai === 'number' && !isNaN(mai) ? mai : 0;
  
  // Calculate MAI percentage for progress bar
  const maiPercentage = Math.min(Math.max(maiValue, 0), 100);
  
  // Get MAI color based on value
  const getMaiColor = (value: number): string => {
    if (value >= 70) return '#ef4444'; // Hot - Red
    if (value >= 55) return '#f59e0b'; // Strong - Orange  
    if (value >= 45) return '#10b981'; // Balanced - Green
    if (value >= 30) return '#3b82f6'; // Buyer - Blue
    return '#6b7280'; // Cold - Gray
  };

  const maiColor = getMaiColor(maiValue);

  if (loading) {
    return (
      <Card 
        className="h-100 border-0"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          borderRadius: '16px'
        }}
      >
        <Card.Body>
          <LoadingShimmer />
        </Card.Body>
      </Card>
    );
  }

  if (!temperature || maiValue === 0) {
    return (
      <Card 
        className="h-100 border-0"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          borderRadius: '16px'
        }}
      >
        <Card.Body className="text-center d-flex flex-column justify-content-center">
          <div style={{ fontSize: '3rem', opacity: 0.3 }}>📊</div>
          <h6 className="text-muted mt-2 mb-0">No Data Available</h6>
          <small className="text-muted">Select a ZIP code to view market temperature</small>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="position-relative">
      <Card 
        className={`h-100 border-0 ${isHotMarket ? 'hot-market-glow' : ''}`}
        style={{
          background: tempConfig?.bgGradient || 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: `1px solid ${tempConfig?.color}40`,
          borderRadius: '16px',
          boxShadow: isHotMarket 
            ? `0 0 30px ${tempConfig?.color}30` 
            : '0 4px 6px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          ...(isHotMarket && {
            animation: 'pulseGlow 3s ease-in-out infinite'
          })
        }}
      >
        <Card.Body className="text-center p-4">
          {/* Temperature Icon */}
          <div className="position-relative mb-3">
            <div 
              className="d-inline-block"
              style={{
                fontSize: '4rem',
                lineHeight: 1,
                filter: isHotMarket ? 'drop-shadow(0 0 10px rgba(239, 68, 68, 0.6))' : 'none',
                animation: isHotMarket ? 'bounce 2s ease-in-out infinite' : 'none'
              }}
            >
              {displayEmoji}
            </div>
            
            {/* AI indicator */}
            <div 
              className="position-absolute"
              style={{
                top: '-5px',
                right: '-5px',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#00d4ff',
                boxShadow: '0 0 10px rgba(0, 212, 255, 0.5)',
                animation: 'pulse 2s infinite'
              }}
            />
          </div>

          {/* Temperature Title */}
          <h5 
            className="mb-2 fw-bold"
            style={{ 
              color: tempConfig?.color,
              textShadow: `0 0 10px ${tempConfig?.color}30`,
              fontSize: '1.1rem'
            }}
          >
            {temperature}
          </h5>

          {/* Temperature Description */}
          <p 
            className="small text-muted mb-3"
            style={{ fontSize: '0.85rem' }}
          >
            {tempConfig?.description}
          </p>

          {/* Market Action Index Section */}
          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon 
                  icon={faChartLine} 
                  className="me-2"
                  style={{ 
                    color: maiColor,
                    fontSize: '14px'
                  }}
                />
                <span 
                  className="small fw-semibold"
                  style={{ color: '#1f2937' }}
                >
                  Market Action Index
                </span>
              </div>
              <span 
                className="fw-bold"
                style={{ 
                  color: maiColor,
                  fontSize: '1.1rem'
                }}
              >
                {maiValue.toFixed(1)}
              </span>
            </div>

            {/* MAI Progress Bar */}
            <div className="position-relative">
              <ProgressBar 
                now={maiPercentage}
                style={{ 
                  height: '8px',
                  borderRadius: '4px',
                  background: 'rgba(0, 0, 0, 0.1)'
                }}
              >
                <ProgressBar 
                  now={maiPercentage}
                  style={{
                    background: `linear-gradient(90deg, ${maiColor}80 0%, ${maiColor} 100%)`,
                    borderRadius: '4px',
                    boxShadow: `0 0 10px ${maiColor}40`
                  }}
                />
              </ProgressBar>
              
              {/* MAI Scale Labels */}
              <div className="d-flex justify-content-between mt-2">
                <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
                  Cold (0-30)
                </span>
                <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
                  Balanced (30-60)
                </span>
                <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
                  Hot (60-100)
                </span>
              </div>
            </div>
          </div>

          {/* Market Intensity Indicator */}
          <div className="mt-3">
            <div className="d-flex justify-content-center">
              {[1, 2, 3].map((level) => (
                <div
                  key={level}
                  className="mx-1"
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: tempConfig && 
                      ((tempConfig.intensity === 'high' && level <= 3) ||
                       (tempConfig.intensity === 'medium' && level <= 2) ||
                       (tempConfig.intensity === 'low' && level <= 1))
                      ? tempConfig.color
                      : 'rgba(107, 114, 128, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>
            <div className="text-center mt-1">
              <span 
                className="small"
                style={{ 
                  fontSize: '0.7rem',
                  color: tempConfig?.color || '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '600'
                }}
              >
                {tempConfig?.intensity} Intensity
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Custom CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes pulseGlow {
          0%, 100% { 
            box-shadow: 0 0 20px ${tempConfig?.color}30, 0 0 40px ${tempConfig?.color}20;
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 30px ${tempConfig?.color}50, 0 0 60px ${tempConfig?.color}30;
            transform: scale(1.02);
          }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0, 0, 0);
          }
          40%, 43% {
            transform: translate3d(0, -8px, 0);
          }
          70% {
            transform: translate3d(0, -4px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }
        
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        
        .hot-market-glow:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px ${tempConfig?.color}40, 0 0 50px ${tempConfig?.color}30 !important;
        }
      `}</style>
    </div>
  );
};

export default MarketTemperatureCard;